/**
 * @file        PageLayout.jsx
 * @module      layouts/PageLayout
 * @description Top-level authenticated layout. Renders the sidebar, header and the
 *              role-aware routing table for the app. Routes are filtered through the
 *              ACL (Access Control List) so each user role only mounts the routes it
 *              is permitted to access, and an on-mount guard redirects users away from
 *              URLs their role cannot view.
 * @author      FreightSee Frontend Team
 * @date        2026-06-22
 */
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Header from "./Header";
import MainContent from "./MainContent";
import Sidebar from "./Sidebar";
import { Skeleton, Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { UNAUTHORIZED_BOOKING_MESSAGE } from "../components/common/UnauthorizedBookingModal";
import { fetchData } from "../store/Authentication/RolesSlice";
import { kybstatus, kybstatusreason, kycdocstatus } from "../store/User/KycSlice";
import { isKybApproved, KYB_GATED_ROLES } from "../utils/kyb";
import MyCredits from "../components/User/MyCredits";
import LearnCreditLimit from "../components/User/LearnCreditLimit";
import { useDispatch, useSelector } from "react-redux";
import { routs } from "../routes/routes";
import { ACL_ROUTE } from "../utils/constant";
import ManualQuoteManagement from "../pages/ManualQuoteManagement";
import ManualQuoteManagementV2 from "../pages/ManualQuoteManagementV2";
import OceanManualQuoteManagement from "../pages/OceanManualQuoteManagement";
import NewSearchRatesResults from "../components/SearchRates/NewSearchRatesResult";
import ManageSocketIoContext from "../ManageSocketContext";
import Newaircargodetail from "../components/BookingHistory/Newaircargodetail";
import BookingDetailPage from "../pages/BookingDetailPage";
import BookingConfirmationPage from "../pages/BookingConfirmationPage";
import NewBookingFlowPage from "../pages/NewBookingFlowPage";
import OceanMainRateCom from "../forms/SearchRates/ShipmentRate/OceanMainRateCom";
import OceanMainRateComNew from "../forms/SearchRates/ShipmentRate/OceanMainRateComNew";
import OceanPaymentSuccesspage from "../forms/SearchRates/ShipmentRate/OceanPaymentSuccesspage";
import KycStatusRibbon from "../components/common/KycStatusRibbon";
import usertour from "usertour.js";
import config from "../config";
import { authuser } from "../store/Authentication/AuthSlice";

// Calling usertour.init() more than once races the vendor SDK's async setup
// against the immediately-following identify() call once the real script has
// loaded, throwing "You must call usertour.init() first". init() must run
// exactly once per page session; identify() is safe to call on every user change.
let usertourInitialized = false;
const Bookingdeletedetails = lazy(() =>
  import("../components/BookingHistory/Bookingdeletepage/Bookingdeletedetails")
);
const OceanCargoDetails = lazy(() =>
  import("../components/BookingHistory/OceanCargoDetails")
);
const QueriesDetails = lazy(() =>
  import("../components/Queries/QueriesDetails")
);
const MobHeader = lazy(() => import("./MobHeader"));
const Profile = lazy(() => import("../components/User/Profile"));
const KYC = lazy(() => import("../components/User/KYC"));
const KybFlowPage = lazy(() => import("../modules/kyb"));
const HelpAndSupport = lazy(() => import("../components/User/HelpAndSupport"));
const Settings = lazy(() => import("../components/User/Settings"));
const TermsAndConditions = lazy(() =>
  import("../components/User/TermsAndConditions")
);
const UserPageLayout = lazy(() => import("./UserPageLayout"));

const PageLayout = () => {
  const userrole = useSelector(authuser);
  const [open, setOpen] = useState(true);
  const [, setRibbonHeight] = useState(0);
  const [userRole, setUserRole] = useState("");
  // Set when someone opens a booking deep-link their role isn't meant to open
  // (a KAM email opened by a non-KAM, or a customer email opened by a KAM) —
  // keeps the "Access Denied" popup below open AFTER the redirect has stripped
  // ?type= off the URL, which is what makes the derived check go false again.
  // No companion message state: both sides show UNAUTHORIZED_BOOKING_MESSAGE.
  const [accessDenied, setAccessDenied] = useState(false);
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const data = useSelector((state) => state.data?.data?.data?.user?.userroles);
  // Business verification (KYB) status drives the ribbon — src/utils/kyb.js.
  const kybStatus = useSelector(kybstatus);
  const kybReason = useSelector(kybstatusreason);
const usertourUser = useSelector(authuser); 


useEffect(() => {
  if (!usertourUser?._id || !config.REACT_APP_USERTOUR_TOKEN) return;

  if (!usertourInitialized) {
    usertour.init(config.REACT_APP_USERTOUR_TOKEN);
    usertourInitialized = true;
  }
  usertour.identify(usertourUser._id, {
    name: usertourUser.full_name,
    email: usertourUser.email,
    role: usertourUser.userroles,
    signed_up_at: usertourUser.createdAt,
  });
}, [usertourUser]);


  useEffect(() => {
    dispatch(fetchData());
    // Load the KYC verification status here (not only on Search Rates). The top
    // KYC ribbon reads state.usersection.kyb_verification_status; previously only the
    // search pages dispatched kycdocstatus(), so landing straight on another page
    // (e.g. Profile from the /home landing) showed no ribbon until the user
    // passed through Search Rates. Fetching it on every PageLayout mount makes the
    // ribbon appear consistently regardless of which page the user enters on.
    dispatch(kycdocstatus());
  }, [dispatch]);

  // Business verification (KYB) changes the KYC status — e.g. approval hides
  // the "KYC Verification Pending" ribbon and enables Book. The KYB page
  // fires "kyb:status-changed" whenever its result changes; reload the status.
  useEffect(() => {
    const reload = () => dispatch(kycdocstatus());
    window.addEventListener("kyb:status-changed", reload);
    return () => window.removeEventListener("kyb:status-changed", reload);
  }, [dispatch]);

  // The KYB team can change the status at any time (approve, move back to
  // review): refresh it on every page change too, so the ribbon and the Book
  // buttons don't stay stale until a full reload.
  useEffect(() => {
    dispatch(kycdocstatus());
  }, [dispatch, pathname]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1440) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    }
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);
  });

  /**
   * KAM email deep-link guard.
   *
   * The KAM "Action Required: Customer Booking Awaiting Confirmation" emails link
   * to booking pages with `?type=kam` (air: /booking-view, ocean:
   * /booking-history/ocean-cargo-booking-details). Those pages are a customer's
   * booking view — the assigned KAM is allowed to open them, but anyone else who
   * lands on the link (e.g. the customer it was forwarded to) should be blocked
   * with a clear "Access Denied" popup rather than silently viewing it.
   *
   * Derived DURING RENDER, not inside an effect. An effect-based check only runs
   * after the first commit, so the booking page had already mounted, fired its
   * detail API call and painted the customer's full booking behind the modal —
   * "Access Denied" while the whole booking was readable underneath it. Computing
   * it here lets us skip mounting the routed page in the same pass (see the
   * `<Routes>` gate below), so nothing unauthorized ever renders.
   *
   * Declared ahead of the ACL guard below because that guard has to defer to it.
   */
  // Two mirror cases, both meaning "this viewer must not see the booking behind
  // the link":
  //   • a KAM email link (?type=kam) opened by a non-KAM (e.g. the customer it
  //     was forwarded to) — bounce to their bookings list, and
  //   • a customer email link (?type=user) opened by a KAM — bounce to the KAM
  //     queries page,
  // plus a third, role-specific case added on top of them without altering
  // either: an air_admin opening a notification deep-link of EITHER flavour.
  const linkType = new URLSearchParams(search).get("type");
  const currentRole = userrole?.userroles;
  // "air_admin" sits on the KAM side of this fence, not the customer side. It
  // shares the KAM queries page (routes.js lists /QueriesKAM for both roles) and
  // is deliberately included in the booking confirm flow the KAM emails exist to
  // trigger (OceanCargoDetails.jsx's isKamOrAdmin, NewAircargoDetails.jsx's
  // `type === "kam" || type === "air_admin"`). Testing `!== "kam"` here denied an
  // air_admin the very link they are allowed to action.
  const isKamSide = currentRole === "kam" || currentRole === "air_admin";
  const kamLinkForNonKam = linkType === "kam" && !!currentRole && !isKamSide;
  // Deliberately `=== "kam"`, NOT isKamSide. isKamSide only widens who may OPEN a
  // KAM link; widening who is BLOCKED from a customer link would newly lock
  // air_admin out of bookings it can already open from its own pages (Queries.jsx
  // and BookingDeletetable.jsx embed these same detail components), which is a
  // capability regression rather than the fix.
  const userLinkForKam = linkType === "user" && currentRole === "kam";

  // air_admin: denied on BOTH notification deep-links (?type=kam and ?type=user).
  // It is neither the assigned KAM the KAM email was addressed to nor the
  // customer the customer email was addressed to, so it sees the Access Denied
  // popup with its own /dashboard behind it.
  //
  // Written as its own additive clause rather than by loosening the two tests
  // above so that no other role's path moves: kamLinkForNonKam and
  // userLinkForKam still evaluate exactly as before for kam, customer and
  // customer_admin, and this line can only ever fire for air_admin.
  //
  // It also costs air_admin nothing in-app. The standing worry — that blocking
  // it here would lock it out of bookings it opens from its own pages
  // (Queries.jsx, AppDrawer.jsx, BookingDeletetable.jsx) — does not apply,
  // because none of those append `?type=`; every in-app booking link is
  // `?queryId=<id>` alone (e.g. Queries.jsx `/booking-view?queryId=...`).
  // `?type=` is stamped only by the notification emails, so the in-app routes
  // fall straight through to the normal page and the confirm flow those pages
  // implement for air_admin (OceanCargoDetails' isKamOrAdmin) is untouched.
  const emailLinkForAirAdmin =
    (linkType === "kam" || linkType === "user") && currentRole === "air_admin";

  const blockedDeepLink =
    kamLinkForNonKam || userLinkForKam || emailLinkForAirAdmin;

  // Where to bounce the blocked viewer. The target must be a route THIS role can
  // actually mount: getAuthorizedRoutes below only registers routes listed in the
  // role's ACL, so hardcoding /booking-history dropped KAM/air_admin sessions on
  // an unregistered route — a blank page behind the popup. (Same trap
  // OceanCargoDetails.jsx works around when it has no queryId.) Prefer the page
  // that fits the role, then fall back to their ACL's own first entry.
  const allowedForRole = ACL_ROUTE[currentRole];
  const firstAllowed = (preferred) =>
    allowedForRole?.includes(preferred) ? preferred : allowedForRole?.[0];
  // air_admin is checked FIRST so both blocked link types leave it on the admin
  // dashboard (/dashboard — where its own login lands it, DashboardPage.jsx).
  // It needs its own branch because /QueriesKAM is in BOTH ACLs, so falling into
  // the KAM branch below would have parked it on the KAM queries page, and
  // because its ACL has no /booking-history for the other branch to resolve.
  // The two original branches are left exactly as they were for every other role.
  const blockedRedirect = emailLinkForAirAdmin
    ? firstAllowed("/dashboard")
    : userLinkForKam
    ? firstAllowed("/QueriesKAM")
    : firstAllowed("/booking-history");

  /**
   * Route access guard (runs once on mount, e.g. on a hard refresh).
   *
   * Reads the cached `user` from localStorage and checks the current pathname
   * against the routes allowed for that user's role (ACL_ROUTE[userroles]).
   * If the user is missing or the current URL is not permitted, it redirects to
   * the role's first allowed route.
   *
   * WHY the explicit fallback check: previously this called
   * `navigate(ACL_ROUTE[user?.userroles]?.[0])` unconditionally. When `userroles`
   * was missing (e.g. after a profile save had overwritten the cached user object),
   * the fallback resolved to `undefined` and the app navigated to the literal
   * "/undefined" URL. We now only redirect when a real fallback route exists,
   * so an unknown/missing role leaves the current URL untouched on refresh.
   */
  useEffect(() => {
    const user = userrole;
    const allowedRoutes = ACL_ROUTE[user?.userroles];
    setUserRole(user?.userroles);
    // Defer to the deep-link guard when it has claimed this navigation: it has
    // already raised the popup and picked a landing page the role can mount. This
    // guard runs later (passive effect vs. layout effect) and still holds the
    // MOUNT pathname in its closure — the link the deep-link guard just replaced —
    // so without this bail it overwrites that destination with ACL_ROUTE[role][0].
    // Since /booking-view is no longer in the customer ACL, that meant a customer
    // bounced off a KAM /booking-view link landed on /search-rates instead of
    // /booking-history. A ?type=-less URL still falls through and is guarded as
    // before, so hand-typing a disallowed path is unaffected.
    if (blockedDeepLink) return;
    // The air/ocean rate-results pages render ENTIRELY from what the navigation
    // that opened them handed over — redux `newsearchformdata` for air
    // (NewSearchRatesResult), router state for ocean (OceanMainRateCom) — and
    // never refetch from the path alone. A refresh (or a pasted/bookmarked URL)
    // wipes the store, so the page comes back with no rates under the query bar.
    // They only sit in the customer ACLs so the quotation-email deep links
    // (`?queryId=<id>`) can be opened cold — those DO bootstrap themselves from
    // the id and survive a reload. So on mount a queryId-less results URL is
    // treated as not-allowed, which restores the pre-ACL behaviour of bouncing a
    // refresh back to the role's fallback route (/search-rates for a customer).
    // In-app navigation is untouched: this layout mounts once per page load, so
    // the guard never sees a client-side push into these routes.
    const isRateResultPath =
      pathname === "/search-rates/search-rates-result" ||
      pathname === "/search-rates/ocean-rates-result";
    const staleRateResult =
      isRateResultPath && !new URLSearchParams(search).get("queryId");
    if (!user || staleRateResult || !allowedRoutes?.includes(`${pathname}`)) {
      const fallbackRoute = allowedRoutes?.[0];
      if (fallbackRoute) {
        return navigate(fallbackRoute, staleRateResult ? { replace: true } : undefined);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // dependency added here

  // Bounce the unauthorized viewer off the link immediately, so the page sitting
  // behind the modal is their own landing page rather than a blank placeholder.
  // `replace` keeps the link out of history (Back would otherwise re-trigger this
  // whole guard).
  //
  // NOTE: the redirect drops `?type=`, which makes `blockedDeepLink` false on the
  // next pass. That is why `accessDenied` is sticky state that only
  // handleAccessDeniedOk clears — resetting it from this effect would slam the
  // popup shut the instant we navigated, before the user ever read it.
  //
  // MUST be useEffect, NOT useLayoutEffect. react-router 6.6.2's useNavigate
  // gates on an `activeRef` it sets in its own PASSIVE effect and silently
  // returns when that ref is still false ("You should call navigate() in a
  // React.useEffect(), not when your component is first rendered."). A layout
  // effect runs before that, so the navigate() call was dropped on the floor
  // while setAccessDenied still applied — the popup appeared but the URL kept the
  // deep link and the page under it never changed. The paint-flash that layout
  // timing was meant to avoid is instead handled by deriving the popup's `open`
  // from blockedDeepLink, so it is already on screen in the very first frame.
  useEffect(() => {
    if (!blockedDeepLink) return;
    setAccessDenied(true);
    // Only navigate to a real route — an unknown role resolves to undefined and
    // navigate() would send the app to the literal "/undefined" URL (the same bug
    // the ACL guard above documents). The popup and the `<Routes>` gate still
    // apply, so nothing unauthorized renders even if we can't pick a landing page.
    if (blockedRedirect) navigate(blockedRedirect, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedDeepLink]);

  // The redirect above already moved the user to their landing page, so OK only
  // has to dismiss the popup.
  const handleAccessDeniedOk = () => {
    setAccessDenied(false);
  };

  // Build the route set this user is allowed to mount: keep only routes whose
  // path is listed in the ACL for the current role (`data` = userroles from store).
  // This ensures unauthorized routes are never even registered for the role.
  const getAuthorizedRoutes = routs.filter((route) =>
    ACL_ROUTE[data]?.includes(route.path)
  );

  return (
    <div className="p-5 pt-0 sm:p-0 relative bg-[#F8F9FA] min-h-screen">
      {/* Access Denied popup — shown when a KAM email (?type=kam) is opened by a
          non-KAM, a customer email (?type=user) is opened by a KAM, or either
          email is opened by an air_admin.
          Title, button and copy are kept identical to UnauthorizedBookingModal
          (the same popup raised by the backend's 404) so the two never diverge.

          `open` is blockedDeepLink OR the sticky state, not the state alone: the
          derived half puts the popup on screen in the FIRST painted frame, and
          the state half keeps it there after the redirect strips ?type=. */}
      <Modal
        open={blockedDeepLink || accessDenied}
        centered
        closable={false}
        maskClosable={false}
        keyboard={false}
        title={
          <span className="flex items-center gap-2">
            <ExclamationCircleFilled style={{ color: "#faad14" }} />
            Access Denied
          </span>
        }
        /* Explicit footer button rather than antd's okText — Tailwind's preflight
           resets `button { background-color: transparent }`, which renders
           .ant-btn-primary white-on-white. Same classes as
           UnauthorizedBookingModal, and justify-end puts OK at bottom-right. */
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              autoFocus
              onClick={handleAccessDeniedOk}
              className="px-6 py-2 text-sm font-semibold text-white bg-[#f1b21b] hover:bg-[#e0a518] rounded-lg transition-colors"
            >
              OK
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-700">{UNAUTHORIZED_BOOKING_MESSAGE}</p>
      </Modal>

      {/* Fixed KYC ribbon */}
      <div className="flex w-full h-full">
        <Sidebar open={open} setOpen={setOpen} />

        <div
          className={
            userrole?.userroles === "customer_admin" ||
            userrole?.userroles === "customer" ||
            userrole?.userroles === "kam"
              ? "overflow-hidden gap-4 relative flex flex-col w-full transition-all duration-[0.5s] ease-in delay-[0s] sm:ml-0 sm:px-0"
              : `h-full overflow-hidden gap-4 relative flex flex-col pl-10 pr-5 w-full transition-all duration-[0.5s] ease-in delay-[0s] sm:ml-0 sm:px-0 ${open ? 'ml-[260px]' : 'ml-[80px]'}`
          }
          // Business verification: "clip" still hides overflow like overflow-hidden,
          // but doesn't stop the KYB step rail from staying in place (sticky) on scroll.
          style={pathname.startsWith("/user/business-verification") ? { overflow: "clip" } : undefined}
        >
          {data === "air_admin" ? (
            <></>
          ) : (
            <div className="sm:hidden">
              <Header />
              {/* Every customer who must pass KYB (customer_admin and team
                  members) sees the band for their status until it's approved.
                  Nothing renders while the status is still loading (''). */}
              {/* Not on the business verification page itself: the customer is
                  already finishing verification there. */}
              {KYB_GATED_ROLES.includes(userRole) && kybStatus && !isKybApproved(kybStatus) && !pathname.startsWith("/user/business-verification") && (
                <div className="kycAlert">
                  <KycStatusRibbon
                    kybStatus={kybStatus}
                    reason={kybReason}
                    onHeightChange={setRibbonHeight}
                  />
                </div>
              )}
            
            </div>
          )}
          {data === "air_admin" || data === "kam" ? (
            <></>
          ) : (
            <div className="hidden sm:block">
              <Suspense fallback={<Skeleton active />}>
                <MobHeader />
              </Suspense>
            </div>
          )}

          <MainContent>
            <ManageSocketIoContext>
              {/* Blocked deep-link viewer (?type=kam opened by a non-KAM,
                  ?type=user opened by a KAM, or either opened by an air_admin):
                  render NOTHING for this one pass
                  instead of the routed page, so the booking details are never
                  mounted or painted behind the Access Denied modal. Deliberately
                  null and not a <Skeleton> — a loader behind the popup reads as
                  "your page is coming", which is the opposite of what happened.
                  The effect above redirects (dropping ?type=) and the real landing
                  page renders here on the next pass. */}
              {blockedDeepLink ? (
                null
              ) : (
              <Routes>
                {getAuthorizedRoutes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <route.component />
                      </Suspense>
                    }
                  />
                ))}
                <Route
                  exact
                  path="user/*"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <UserPageLayout />
                    </Suspense>
                  }
                >
                  <Route
                    exact
                    path="profile"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <Profile />
                      </Suspense>
                    }
                  />
                  <Route
                    exact
                    path="kyc"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <KYC />
                      </Suspense>
                    }
                  />
                  {/* KYB flow inside the user layout — where the KYC menu items
                      go, and the only place it is served: /business-verification
                      (App.js) redirects here, DigiLocker's ?dl_return=1 included. */}
                  <Route
                    exact
                    path="business-verification"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <KybFlowPage embedded />
                      </Suspense>
                    }
                  />
                  <Route
                    exact
                    path="help-and-support"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <HelpAndSupport />
                      </Suspense>
                    }
                  />
                  <Route
                    exact
                    path="settings"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <Settings />
                      </Suspense>
                    }
                  />
                  <Route
                    exact
                    path="terms-and-conditions"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <TermsAndConditions />
                      </Suspense>
                    }
                  />
                  <Route
                    exact
                    path="my-credits"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <MyCredits />
                      </Suspense>
                    }
                  />
                  <Route
                    exact
                    path="my-credits/learn-credit-limit"
                    element={
                      <Suspense fallback={<Skeleton active />}>
                        <LearnCreditLimit />
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  exact
                  path="search-rates/search-rates-result"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <NewSearchRatesResults />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="search-rates/booking-confirmation"
                  element={<BookingConfirmationPage />}
                />
                <Route
                  exact
                  path="search-rates/new-booking-confirmation"
                  element={<NewBookingFlowPage />}
                />
                <Route
                  exact
                  path="search-rates/ocean-rates-result"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <OceanMainRateCom />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="search-rates/ocean-rates-result-new"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <OceanMainRateComNew />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="ocean-booking-payment-succes"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <OceanPaymentSuccesspage />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="queries/queries-details"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <QueriesDetails />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="booking-history/air-booking-details"
                  element={<BookingDetailPage />}
                />
                {/* booking-history/air-cargo-booking-details (AirCargoDetails)
                    is gone — it was a second, older air booking-detail surface
                    reachable only from a Payment notification, which now points
                    at booking-history/air-booking-details like every other air
                    booking link. */}
                <Route
                  exact
                  path="/booking-view"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <Newaircargodetail />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="booking-history/ocean-cargo-booking-details"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <OceanCargoDetails />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="send-manual-quote"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <ManualQuoteManagement />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="send-manual-quote-v2"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <ManualQuoteManagementV2 />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="send-manual-quote-ocean"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <OceanManualQuoteManagement />
                    </Suspense>
                  }
                />
                <Route
                  exact
                  path="/bookingdeletehistory/booking-deleted-details"
                  element={
                    <Suspense fallback={<Skeleton active />}>
                      <Bookingdeletedetails />
                    </Suspense>
                  }
                />
              </Routes>
              )}
            </ManageSocketIoContext>
            <Outlet />
          </MainContent>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PageLayout);